import React from 'react';
import { Link } from 'react-router-dom';
import { useBrandKit } from '../../contexts/BrandKitContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiStar, FiSettings, FiImage, FiDroplet } = FiIcons;

const LOCKED_COLORS = [
  { name: 'Primary', hex: '#03245E' },
  { name: 'Accent', hex: '#E2C164' },
  { name: 'Background', hex: '#F3F7FA' },
  { name: 'Secondary', hex: '#D7B740' },
  { name: 'Highlight', hex: '#F7EFE2' }
];

const LOCKED_FONTS = [
  { role: 'Titles', font: 'Anton' },
  { role: 'Body', font: 'Helvetica World' },
  { role: 'Subtitle', font: 'Montserrat' },
  { role: 'Heading', font: 'League Spartan' },
  { role: 'Highlight', font: 'Gistesy' }
];

export default function EffectiveBrandKit() {
  const { currentBrandKit } = useBrandKit();
  
  const displayKit = currentBrandKit || {
    name: "Default Brand Kit",
    logos: 0,
    watermarks: 0,
    modified: new Date().toLocaleString()
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Effective Brand Kit (Live from Server)</h2>
        </div>
        <Link 
          to="/brand-kit" 
          className="flex items-center px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <SafeIcon icon={FiSettings} className="w-4 h-4 mr-2" />
          Manage Brand Kit
        </Link>
      </div>

      <div className="border rounded-md p-6">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900">{displayKit.name}</h3>
            <SafeIcon icon={FiLock} className="w-4 h-4 text-gray-400" />
            <span className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
              Active
            </span>
          </div>
        </div>
        
        <p className="text-sm text-gray-500 mb-6">
          Professional brand • Last modified: {displayKit.modified}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Colors & Typography */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-700 flex items-center mb-3">
                <SafeIcon icon={FiLock} className="w-4 h-4 mr-2 text-gray-400" />
                Locked Brand Colors
              </h4>
              <ul className="space-y-2 text-sm">
                {LOCKED_COLORS.map(color => (
                  <li key={color.name} className="flex items-center">
                    <span 
                      style={{ backgroundColor: color.hex }} 
                      className="w-4 h-4 rounded-sm mr-2 border border-gray-300"
                    />
                    <span className="text-gray-800 mr-2">{color.name}:</span>
                    <span className="text-gray-500">{color.hex}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-700 flex items-center mb-3">
                <SafeIcon icon={FiLock} className="w-4 h-4 mr-2 text-gray-400" />
                Locked Typography
              </h4>
              <ul className="space-y-1.5 text-sm">
                {LOCKED_FONTS.map(type => (
                  <li key={type.role}>
                    <span className="text-gray-800 mr-2">{type.role}:</span>
                    <span className="text-gray-500">{type.font}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Assets */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-700 flex items-center mb-3">
                <SafeIcon icon={FiImage} className="w-4 h-4 mr-2 text-gray-400" />
                Logo Assets
              </h4>
              <p className="text-sm text-gray-600 mb-2">
                {displayKit.logos?.length || 0} logos
              </p>
              <div className="border rounded-md p-4 text-center text-sm text-gray-400 bg-gray-50">
                {displayKit.logos?.length > 0 ? (
                  <div>
                    <SafeIcon icon={FiImage} className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                    <span>{displayKit.logos.length} logo(s) uploaded</span>
                  </div>
                ) : (
                  'No logos uploaded'
                )}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-700 flex items-center mb-3">
                <SafeIcon icon={FiDroplet} className="w-4 h-4 mr-2 text-gray-400" />
                Watermark Assets
              </h4>
              <p className="text-sm text-gray-600 mb-2">
                {displayKit.watermarks?.length || 0} watermarks
              </p>
              <div className="border rounded-md p-4 text-center text-sm text-gray-400 bg-gray-50">
                {displayKit.watermarks?.length > 0 ? (
                  <div>
                    <SafeIcon icon={FiDroplet} className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                    <span>{displayKit.watermarks.length} watermark(s) uploaded</span>
                  </div>
                ) : (
                  'No watermarks uploaded'
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}